// src/store/cartStore.js
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import config from '../config';

export const useCartStore = create(
  persist(
    (set, get) => ({
      items: [],
      
      addItem: (product) => {
        const items = get().items;
        const existingItem = items.find((item) => item.id === product.id && !item.packageId);
        
        if (existingItem) {
          set({
            items: items.map((item) =>
              item.id === product.id && !item.packageId
                ? { ...item, quantity: item.quantity + 1 }
                : item
            ),
          });
        } else {
          set({
            items: [...items, { ...product, quantity: 1, packageId: null }],
          });
        }
      },
      
      // ✅ اضافه کردن محصولات یک پکیج به سبد خرید
      addPackage: async (packageData, fetchProductDetails) => {
        const items = get().items;
        const newItems = [...items];
        const productIds = packageData.items.map(item => item.product);
        
        // گرفتن جزییات قیمت محصولات از API (اگه تابع داده شده)
        let productDetails = {};
        if (fetchProductDetails) {
          try {
            productDetails = await fetchProductDetails(productIds);
          } catch (error) {
            console.error('Error fetching product details:', error);
          }
        }
        
        // اضافه کردن هر محصول پکیج به سبد خرید
        for (const packageItem of packageData.items) {
          const productId = packageItem.product;
          const quantity = packageItem.quantity;
          const productName = packageItem.product_name;
          const productImage = packageItem.product_image;
          
          // قیمت محصول - اگر از API گرفته شده استفاده کن، وگرنه از قیمت تقریبی پکیج
          let productPrice = productDetails[productId]?.final_price || 
                            (packageData.total_price / packageData.items.length);
          
          const existingItemIndex = newItems.findIndex(
            item => item.id === productId && item.packageId === packageData.id
          );
          
          const cartItem = {
            id: productId,
            name: productName,
            final_price: productPrice,
            quantity: quantity,
            main_image: productImage ? config.getMediaUrl(productImage) : '/placeholder-product.jpg',
            packageId: packageData.id,  // شناسه پکیج برای شناسایی آیتم‌های یک پکیج
            packageName: packageData.name,
            isPackageItem: true,
            originalQuantity: quantity, // مقدار اصلی برای tracking
          };
          
          if (existingItemIndex !== -1) {
            // اگه قبلاً این محصول از این پکیج اضافه شده، تعداد رو زیاد کن
            newItems[existingItemIndex].quantity += quantity;
          } else {
            newItems.push(cartItem);
          }
        }
        
        set({ items: newItems });
        return true;
      },
      
      // ✅ اضافه کردن پکیج بدون fetch (با استفاده از قیمت‌های تقریبی)
      addPackageSimple: (packageData) => {
        const items = get().items;
        const newItems = [...items];
        
        // محاسبه قیمت تقریبی هر محصول در پکیج
        const approxPricePerItem = packageData.final_price / packageData.items.length;
        
        for (const packageItem of packageData.items) {
          const productId = packageItem.product;
          const quantity = packageItem.quantity;
          
          const existingItemIndex = newItems.findIndex(
            item => item.id === productId && item.packageId === packageData.id
          );
          
          const cartItem = {
            id: productId,
            name: packageItem.product_name,
            final_price: Math.round(approxPricePerItem),
            quantity: quantity,
            main_image: packageItem.product_image ? 
              config.getMediaUrl(packageItem.product_image) : 
              '/placeholder-product.jpg',
            packageId: packageData.id,
            packageName: packageData.name,
            isPackageItem: true,
          };
          
          if (existingItemIndex !== -1) {
            newItems[existingItemIndex].quantity += quantity;
          } else {
            newItems.push(cartItem);
          }
        }
        
        set({ items: newItems });
        return true;
      },
      
      // ✅ حذف کل محصولات یک پکیج از سبد خرید
      removePackage: (packageId) => {
        set({
          items: get().items.filter((item) => item.packageId !== packageId),
        });
      },
      
      removeItem: (productId, packageId = null) => {
        set({
          items: get().items.filter((item) => {
            if (packageId) {
              // حذف آیتم خاص از یک پکیج خاص
              return !(item.id === productId && item.packageId === packageId);
            }
            // حذف آیتم معمولی (غیر پکیج)
            return item.id !== productId;
          }),
        });
      },
      
      updateQuantity: (productId, quantity, packageId = null) => {
        if (quantity <= 0) {
          get().removeItem(productId, packageId);
          return;
        }
        
        set({
          items: get().items.map((item) => {
            if (packageId) {
              if (item.id === productId && item.packageId === packageId) {
                return { ...item, quantity };
              }
            } else {
              if (item.id === productId && !item.packageId) {
                return { ...item, quantity };
              }
            }
            return item;
          }),
        });
      },
      
      clearCart: () => set({ items: [] }),
      
      getTotalItems: () => {
        return get().items.reduce((total, item) => total + item.quantity, 0);
      },
      
      getTotalPrice: () => {
        return get().items.reduce(
          (total, item) => total + (item.final_price || 0) * item.quantity,
          0
        );
      },
      
      // ✅ گروه‌بندی آیتم‌ها بر اساس پکیج
      getGroupedItems: () => {
        const items = get().items;
        const packages = {};
        const regularItems = [];
        
        items.forEach(item => {
          if (item.packageId) {
            if (!packages[item.packageId]) {
              packages[item.packageId] = {
                packageId: item.packageId,
                packageName: item.packageName,
                items: [],
                totalPrice: 0,
              };
            }
            packages[item.packageId].items.push(item);
            packages[item.packageId].totalPrice += (item.final_price || 0) * item.quantity;
          } else {
            regularItems.push(item);
          }
        });
        
        return {
          packages: Object.values(packages),
          regularItems,
        };
      },
    }),
    {
      name: 'cart-storage',
    }
  )
);
